import React, {useLayoutEffect, useRef, useState} from "react"

import {updateOwnerNotes} from "../api/properties"

type UseOwnerNotesOptions = {
   propertyId: string
   ownerNotes?: string
   onSaved: (ownerNotes: string | null) => void
}

type OwnerNotesResult = {
   editingOwnerNotes: boolean
   ownerNotesDraft: string
   setOwnerNotesDraft: React.Dispatch<React.SetStateAction<string>>
   savingOwnerNotes: boolean
   ownerNotesError: string
   startEditingOwnerNotes: () => void
   cancelEditingOwnerNotes: () => void
   saveOwnerNotes: () => Promise<void>
}

type OwnerNotesEditorState = {
   propertyId: string
   editing: boolean
   draft: string
   saving: boolean
   error: string
}

const createEditorState = (propertyId: string): OwnerNotesEditorState => ({
   propertyId,
   editing: false,
   draft: "",
   saving: false,
   error: "",
})

export function useOwnerNotes({
   propertyId,
   ownerNotes,
   onSaved,
}: UseOwnerNotesOptions): OwnerNotesResult {
   const [editorState, setEditorState] = useState(() => createEditorState(propertyId))
   const propertyScopeRef = useRef({propertyId, generation: 0})
   const saveRequestIdRef = useRef(0)

   // State is tagged with its property so a previous property's draft is never rendered for this one.
   const currentState =
      editorState.propertyId === propertyId ? editorState : createEditorState(propertyId)

   // Treat every committed property change as a new editor session, including A -> B -> A.
   useLayoutEffect(() => {
      if (propertyScopeRef.current.propertyId !== propertyId) {
         propertyScopeRef.current = {
            propertyId,
            generation: propertyScopeRef.current.generation + 1,
         }
         setEditorState(createEditorState(propertyId))
      }
   }, [propertyId])

   const updateCurrentState = (update: (state: OwnerNotesEditorState) => OwnerNotesEditorState) => {
      setEditorState((previousState) => {
         const state =
            previousState.propertyId === propertyId ? previousState : createEditorState(propertyId)
         return update(state)
      })
   }

   const startEditingOwnerNotes = () => {
      updateCurrentState((state) => ({
         ...state,
         editing: true,
         draft: ownerNotes ?? "",
         error: "",
      }))
   }

   const cancelEditingOwnerNotes = () => {
      updateCurrentState((state) => ({
         ...state,
         editing: false,
         draft: "",
         error: "",
      }))
   }

   const setOwnerNotesDraft: OwnerNotesResult["setOwnerNotesDraft"] = (value) => {
      updateCurrentState((state) => ({
         ...state,
         draft: typeof value === "function" ? value(state.draft) : value,
      }))
   }

   const saveOwnerNotes = async () => {
      const requestPropertyId = propertyId
      const requestGeneration = propertyScopeRef.current.generation
      const requestId = saveRequestIdRef.current + 1
      saveRequestIdRef.current = requestId
      const draftToSave = currentState.draft

      const isCurrentRequest = () =>
         propertyScopeRef.current.propertyId === requestPropertyId &&
         propertyScopeRef.current.generation === requestGeneration &&
         saveRequestIdRef.current === requestId

      updateCurrentState((state) => ({...state, saving: true, error: ""}))

      try {
         const {ownerNotes: savedOwnerNotes} = await updateOwnerNotes(
            requestPropertyId,
            draftToSave,
         )
         if (!isCurrentRequest()) return

         onSaved(savedOwnerNotes)
         updateCurrentState((state) => ({
            ...state,
            draft: savedOwnerNotes ?? "",
            editing: false,
            saving: false,
         }))
      } catch (error) {
         if (!isCurrentRequest()) return

         console.error("Error actualizando notas al propietario", error)
         updateCurrentState((state) => ({
            ...state,
            saving: false,
            error: "No se pudieron guardar las notas. Intenta de nuevo.",
         }))
      }
   }

   return {
      editingOwnerNotes: currentState.editing,
      ownerNotesDraft: currentState.draft,
      setOwnerNotesDraft,
      savingOwnerNotes: currentState.saving,
      ownerNotesError: currentState.error,
      startEditingOwnerNotes,
      cancelEditingOwnerNotes,
      saveOwnerNotes,
   }
}
